// The record of reviewed games (2026-10-19): the ids of the games whose
// review screen has been opened at least once, kept as `reviewed.json`
// (`{ "ids": [...] }`) in the plugin's storage scope. Pure parsing and a
// small store over the storage API, which the plugin registers as the
// `reviewed` API. Nothing here knows of React, the review or the store.
import type { Reviewed } from '@chess/api-reviewed';
import type { Storage } from '@chess/api-storage';

export const REVIEWED_FILE = 'reviewed.json';

/** The ids from the file's text; undefined for no file or a broken one, which the first-load rule then fills. */
export function parseReviewed(text: string | undefined): string[] | undefined {
  if (!text) return undefined;
  try {
    const raw: unknown = JSON.parse(text);
    const ids = typeof raw === 'object' && raw !== null && 'ids' in raw ? (raw as { ids: unknown }).ids : undefined;
    if (!Array.isArray(ids)) return undefined;
    return ids.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return undefined;
  }
}

/** The file's text for a set of ids. */
export function serializeReviewed(ids: Iterable<string>): string {
  return JSON.stringify({ ids: [...ids] });
}

export type RecordStorage = Pick<Storage, 'readText' | 'write'>;

export interface RecordOptions {
  /**
   * The games that count as reviewed when there is no file yet (the first
   * load): the games the store holds at that moment, so a user who already
   * has games does not see all of them unreviewed.
   */
  firstIds(): Promise<readonly string[]>;
  /** Hears a write that failed; the record in memory stays as it is. */
  onError?(what: string, error: unknown): void;
}

export class ReviewedRecord implements Reviewed {
  private ids = new Set<string>();
  private loaded = false;
  private loading?: Promise<void>;
  private saving: Promise<void> = Promise.resolve();
  private readonly listeners = new Set<() => void>();
  private readonly storage: Promise<RecordStorage>;
  private readonly options: RecordOptions;

  constructor(storage: Promise<RecordStorage>, options: RecordOptions) {
    this.storage = storage;
    this.options = options;
  }

  /** Reads the file once (the first load writes it); later calls return the same promise. */
  ready(): Promise<void> {
    this.loading ??= this.load();
    return this.loading;
  }

  has(gameId: string): boolean {
    return this.ids.has(gameId);
  }

  /** Marks a game reviewed: at once once the record is read, after the read before that. */
  mark(gameId: string): void {
    if (!this.loaded) {
      this.ready().then(
        () => this.mark(gameId),
        () => undefined, // the read's failure is the reader's to report
      );
      return;
    }
    if (this.ids.has(gameId)) return;
    this.ids.add(gameId);
    this.notify();
    this.save();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }

  /** Waits for the writes so far; for tests. */
  flushed(): Promise<void> {
    return this.saving;
  }

  private async load(): Promise<void> {
    const storage = await this.storage;
    const read = parseReviewed(await storage.readText(REVIEWED_FILE));
    if (read) {
      for (const id of read) this.ids.add(id);
    } else {
      for (const id of await this.options.firstIds()) this.ids.add(id);
      this.save();
    }
    this.loaded = true;
    this.notify();
  }

  /** Writes the whole record, one write after another so the last one wins. */
  private save(): void {
    const text = serializeReviewed(this.ids);
    this.saving = this.saving
      .then(() => this.storage)
      .then((s) => s.write(REVIEWED_FILE, text))
      .catch((error: unknown) => this.options.onError?.('the record of reviewed games did not save', error));
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}
